/**
 * Travel Desk reports — read-only aggregates over trips, desk bookings, settlements and credits for
 * HR, finance, the Expense Manager and the desk manager (ADR 0030, plan §5.6). Declared on its own
 * (invariant 8) before the API and the report screens that consume it. Under `/travel/reports`; the
 * traveller's side is `travel-request.contract.ts`, the desk's `travel-desk.contract.ts`, the policy admin
 * `travel-policy.contract.ts`.
 *
 * WHO SEES WHAT. Every route is `travel:read`, and the POPULATION is the caller's own grants of it
 * expanded exactly as the roster resolver expands them (the same rule as the expense reports): an org
 * grant (finance, expense manager, desk manager, HR admin) sees every traveller, a department or legal
 * entity grant (an HR executive) sees the people placed there. A trip, booking, refund or credit belongs
 * to its TRAVELLER, so it is in a report only when the traveller is in the caller's population. A
 * caller with no `travel:read` gets 403. Travel spend is not pay, so ADR 0023's salary visibility is
 * untouched, and no report carries passport data, free text, or anything a traveller typed as a reason.
 *
 * WHICH TRIPS AND WHEN. The range `from`..`to` (both inclusive, at most 366 days — the route refuses
 * more with 422 so every query stays bounded) is read on the date that fits each report, always as a
 * calendar day: trip reports on the trip's `startDate`; desk reports on the day the booking reached the
 * desk (approval); cancellation loss on the day the booking was cancelled; vendor spend and the GST
 * export on the day the booking was confirmed. The two "as of now" reports — refunds pending and unused
 * credits — ignore the range, as the expense advances report does; the entity, department and currency
 * filters still apply. Trips that never reached approval (`draft`, `invited`, `pending`, `rejected`,
 * `withdrawn`, `expired`) are not spend; a trip that was cancelled after approval is, for what it cost.
 *
 * WHERE A TRIP BELONGS. Legal entity, department, business unit and cost centre are the traveller's
 * placement ON THE TRIP'S START DATE (invariant 5 — March's spend is answered by March's org chart),
 * and the matching filters narrow by that same placement. Project, cost centre and business unit are
 * also what the trip's COST SPLIT says (D28, EC-7): for those three dimensions a trip's amounts are
 * spread over its `TravelCostAllocation` rows by percentage, the last row taking the rounding remainder
 * so the parts always add up to the whole. A trip with no split belongs entirely to the traveller's
 * own cost centre and business unit and to no project (the null "Unassigned" group). A trip in two
 * groups counts once in each, so `tripCount` can add up to more than the number of trips; the money
 * never does. The `projectId`, `costCentreId` and `businessUnitId` filters keep a trip when any of its
 * split rows matches and then count only that row's share.
 *
 * WHAT "ACTUAL" IS. Company-paid bookings the desk confirmed, less refunds received, plus cancellation
 * charges the company bore (`bookedAmount`); plus the trip-linked expense claims a settlement approved
 * (`claimedAmount`, so per diem, local conveyance and self-booked fares). `actualAmount` is their sum.
 * `estimatedAmount` is the trip's approved estimate, including any approved amendment. An advance is a
 * cash movement, not spend, and is never in these figures.
 *
 * CURRENCY. Amounts are decimal strings (invariant 2) and every row carries its currency; trip money is
 * in the traveller's legal-entity currency (EC-6). There is no FX facility in this codebase, so nothing
 * is converted and two currencies are never added: every aggregate is grouped by currency and a client
 * shows one total per currency.
 *
 * SCALE. Aggregate reports are bounded by construction (a range, a dimension). The row-level reports
 * are cursor-paginated for screens; the CSV export refuses more than `TRAVEL_REPORT_EXPORT_ROW_CAP`
 * rows with 422 and asks for a narrower range, so a file stays something a spreadsheet can open.
 * Every module-off answer is 404 `module_disabled`, as elsewhere under `/travel` (D33).
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { TRAVEL_CHARGE_BORNE_BY, TRAVEL_VENDOR_TYPES } from '../enums.js';
import {
  currencySchema,
  dateOnlySchema,
  decimalStringSchema,
  errorBodySchema,
  instantSchema,
  paginatedSchema,
  uuidSchema,
} from '../schemas/common.js';
import {
  travelCancelReasonSchema,
  travelEmergencyContactSchema,
  travelPolicyViolationCodeSchema,
  travelPolicyViolationSeveritySchema,
  travelTripStatusSchema,
  travellingQuerySchema,
  travellingRowSchema,
} from '../travel/schemas.js';
import { routeMeta } from './meta.js';

const c = initContract();

const travelChargeBorneBySchema = z.enum(TRAVEL_CHARGE_BORNE_BY);
const travelVendorTypeSchema = z.enum(TRAVEL_VENDOR_TYPES);

// ---------------------------------------------------------------------------
// Query
// ---------------------------------------------------------------------------

/**
 * The filter every report takes. `from`/`to` are required so no report silently means "all time"; that
 * `from <= to` and the span is at most 366 days is checked by the route (422), not here, so the
 * copilot's derived tool keeps a plain object schema. The dimension filters narrow by the placement
 * and cost split described in the header.
 */
export const travelReportQuerySchema = z.object({
  from: dateOnlySchema,
  to: dateOnlySchema,
  legalEntityId: uuidSchema.optional(),
  departmentId: uuidSchema.optional(),
  businessUnitId: uuidSchema.optional(),
  costCentreId: uuidSchema.optional(),
  projectId: uuidSchema.optional(),
  currency: currencySchema.optional(),
});
export type TravelReportQuery = z.infer<typeof travelReportQuerySchema>;

/** A row-level report: the same filter, paged. Stable order per report; the cursor is opaque. */
export const travelReportPageQuerySchema = travelReportQuerySchema.extend({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type TravelReportPageQuery = z.infer<typeof travelReportPageQuerySchema>;

/** `period` is the calendar month of the trip's start date (`YYYY-MM`). */
export const TRAVEL_SPEND_DIMENSIONS = [
  'period',
  'department',
  'business_unit',
  'project',
  'cost_centre',
  'legal_entity',
] as const;
export const travelSpendDimensionSchema = z.enum(TRAVEL_SPEND_DIMENSIONS);
export type TravelSpendDimension = z.infer<typeof travelSpendDimensionSchema>;

export const travelSpendBreakdownQuerySchema = travelReportQuerySchema.extend({
  groupBy: travelSpendDimensionSchema,
});

/** How the desk's turnaround is grouped: by team, by the agent who took the booking, or by month. */
export const TRAVEL_DESK_REPORT_DIMENSIONS = ['team', 'agent', 'period'] as const;
export const travelDeskReportDimensionSchema = z.enum(TRAVEL_DESK_REPORT_DIMENSIONS);
export type TravelDeskReportDimension = z.infer<typeof travelDeskReportDimensionSchema>;

export const travelDeskPerformanceQuerySchema = travelReportQuerySchema.extend({
  groupBy: travelDeskReportDimensionSchema.default('team'),
});

/** A query-string boolean: `?overOnly=true`. (`z.coerce.boolean()` would read "false" as true.) */
const queryBooleanSchema = z
  .enum(['true', 'false'])
  .optional()
  .transform((value) => (value === undefined ? undefined : value === 'true'));

export const travelEstimateVsActualQuerySchema = travelReportPageQuerySchema.extend({
  /** Only trips whose actual spend is above their estimate. */
  overOnly: queryBooleanSchema,
});

export const travelVendorSpendQuerySchema = travelReportQuerySchema.extend({
  vendorType: travelVendorTypeSchema.optional(),
});

/**
 * The datasets a CSV can be downloaded for. Most mirror a report above; `gst` is the register finance
 * claims input tax credit from (D31): one line per company-paid booking the desk confirmed in the
 * range, with the trip reference, booking kind, vendor and its GSTIN, the company's GSTIN, the invoice
 * number and date, the base amount, the tax amount, the total, and the currency — the fields the
 * booking records, no more. `estimate_vs_actual` is one line per trip.
 */
export const TRAVEL_REPORT_DATASETS = [
  'estimate_vs_actual',
  'spend',
  'violations',
  'post_facto',
  'desk_performance',
  'cancellations',
  'refunds_pending',
  'unused_credits',
  'vendor_spend',
  'gst',
] as const;
export const travelReportDatasetSchema = z.enum(TRAVEL_REPORT_DATASETS);
export type TravelReportDataset = z.infer<typeof travelReportDatasetSchema>;

/** A CSV finance can still open in a spreadsheet; past this, narrow the range (422). */
export const TRAVEL_REPORT_EXPORT_ROW_CAP = 20_000;

export const travelReportExportQuerySchema = travelReportQuerySchema.extend({
  dataset: travelReportDatasetSchema,
  /** `spend` only; the route defaults it to `department`. */
  spendGroupBy: travelSpendDimensionSchema.optional(),
  /** `desk_performance` only; the route defaults it to `team`. */
  deskGroupBy: travelDeskReportDimensionSchema.optional(),
  /** `vendor_spend` and `gst` only. */
  vendorType: travelVendorTypeSchema.optional(),
});
export type TravelReportExportQuery = z.infer<typeof travelReportExportQuerySchema>;

// ---------------------------------------------------------------------------
// DTOs — building blocks
// ---------------------------------------------------------------------------

/** Whose travellers the figures cover: everyone, or the people the caller's narrower grants reach. */
export const travelReportScopeSchema = z.object({
  kind: z.enum(['org', 'restricted']),
  /** How many people a `restricted` scope reaches; null for `org`. */
  personCount: z.number().int().nonnegative().nullable(),
});
export type TravelReportScope = z.infer<typeof travelReportScopeSchema>;

const countSchema = z.number().int().nonnegative();

/** A percentage to one decimal place, signed ("12.5", "-3.0"). */
const signedPercentSchema = z.string().regex(/^-?\d{1,5}(\.\d)?$/, 'Use one decimal place');

/** The four money figures every spend row carries; see "WHAT ACTUAL IS" in the header. */
const spendFiguresShape = {
  estimatedAmount: decimalStringSchema,
  /** Confirmed company-paid bookings, less refunds received, plus charges the company bore. */
  bookedAmount: decimalStringSchema,
  /** Trip-linked expense claims a settlement approved. */
  claimedAmount: decimalStringSchema,
  /** `bookedAmount` + `claimedAmount`. */
  actualAmount: decimalStringSchema,
};

// ---------------------------------------------------------------------------
// DTOs — spend
// ---------------------------------------------------------------------------

export const travelSpendSummarySchema = z.object({
  from: dateOnlySchema,
  to: dateOnlySchema,
  scope: travelReportScopeSchema,
  /** One row per currency, never summed across them. */
  totals: z.array(
    z.object({
      currency: currencySchema,
      tripCount: countSchema,
      travellerCount: countSchema,
      ...spendFiguresShape,
      /** Cancellation charges the company bore in the range (part of `bookedAmount`, shown apart). */
      cancellationLossAmount: decimalStringSchema,
    }),
  ),
});
export type TravelSpendSummary = z.infer<typeof travelSpendSummarySchema>;

export const travelSpendBreakdownRowSchema = z.object({
  /** `YYYY-MM` for period, else the department / business unit / project / cost centre / legal entity id; null = Unassigned. */
  key: z.string().nullable(),
  label: z.string(),
  currency: currencySchema,
  /** A trip in several groups counts once in each (header, "WHERE A TRIP BELONGS"). */
  tripCount: countSchema,
  ...spendFiguresShape,
});
export type TravelSpendBreakdownRow = z.infer<typeof travelSpendBreakdownRowSchema>;

export const travelSpendBreakdownSchema = z.object({
  groupBy: travelSpendDimensionSchema,
  /** Ordered by currency, then period ascending or actual amount descending. */
  rows: z.array(travelSpendBreakdownRowSchema),
});
export type TravelSpendBreakdown = z.infer<typeof travelSpendBreakdownSchema>;

// ---------------------------------------------------------------------------
// DTOs — estimate vs actual
// ---------------------------------------------------------------------------

export const travelEstimateVsActualRowSchema = z.object({
  tripId: uuidSchema,
  reference: z.string(),
  personId: uuidSchema,
  personName: z.string(),
  departmentName: z.string().nullable(),
  startDate: dateOnlySchema,
  endDate: dateOnlySchema,
  status: travelTripStatusSchema,
  currency: currencySchema,
  estimatedAmount: decimalStringSchema,
  bookedAmount: decimalStringSchema,
  claimedAmount: decimalStringSchema,
  actualAmount: decimalStringSchema,
  /** `actualAmount` - `estimatedAmount`; positive is over. */
  varianceAmount: decimalStringSchema,
  /** Of the estimate, one decimal place; null when the estimate is zero. */
  variancePct: signedPercentSchema.nullable(),
});
export type TravelEstimateVsActualRow = z.infer<typeof travelEstimateVsActualRowSchema>;

/**
 * Trips by start date, newest first. `totals` cover EVERY trip the filter matches, not just the page.
 * A trip still settling shows what is actual so far; its variance is provisional until it closes.
 */
export const travelEstimateVsActualSchema = paginatedSchema(travelEstimateVsActualRowSchema).extend(
  {
    totals: z.array(
      z.object({
        currency: currencySchema,
        tripCount: countSchema,
        estimatedAmount: decimalStringSchema,
        actualAmount: decimalStringSchema,
        varianceAmount: decimalStringSchema,
        overEstimateTripCount: countSchema,
      }),
    ),
  },
);
export type TravelEstimateVsActual = z.infer<typeof travelEstimateVsActualSchema>;

// ---------------------------------------------------------------------------
// DTOs — violations and post-facto trips
// ---------------------------------------------------------------------------

/**
 * How often each policy rule was broken, read from the evaluation snapshotted on each submitted trip
 * (D7), never re-evaluated. A trip that broke two rules is in two rows.
 */
export const travelViolationsReportSchema = z.object({
  from: dateOnlySchema,
  to: dateOnlySchema,
  scope: travelReportScopeSchema,
  /** Trips submitted for approval in the range. */
  tripCount: countSchema,
  /** Of those, trips with at least one violation. */
  violatingTripCount: countSchema,
  /** Trips filed for a traveller with no grade on the start date, evaluated on the lowest band (EC-49). */
  gradeMissingTripCount: countSchema,
  rows: z.array(
    z.object({
      code: travelPolicyViolationCodeSchema,
      severity: travelPolicyViolationSeveritySchema,
      tripCount: countSchema,
      /** How the trips ended up: approved, rejected; the rest are still open. */
      approvedCount: countSchema,
      rejectedCount: countSchema,
      /** Hard blocks a `travel:approve` holder waived (D24, EC-53). */
      overriddenCount: countSchema,
    }),
  ),
});
export type TravelViolationsReport = z.infer<typeof travelViolationsReportSchema>;

export const travelPostFactoRowSchema = z.object({
  tripId: uuidSchema,
  reference: z.string(),
  personId: uuidSchema,
  personName: z.string(),
  departmentName: z.string().nullable(),
  startDate: dateOnlySchema,
  endDate: dateOnlySchema,
  /** When the request was filed; null on a draft that was never submitted. */
  submittedAt: instantSchema.nullable(),
  /** Whole days from the trip's end to the day it was filed, in the trip's timezone; negative while it was still running. */
  daysAfterEnd: z.number().int().nullable(),
  status: travelTripStatusSchema,
  currency: currencySchema,
  estimatedAmount: decimalStringSchema,
});
export type TravelPostFactoRow = z.infer<typeof travelPostFactoRowSchema>;

/** Trips filed after they began (D14), newest first, with how common that is. */
export const travelPostFactoReportSchema = paginatedSchema(travelPostFactoRowSchema).extend({
  summary: z.object({
    /** Trips submitted for approval in the range. */
    tripCount: countSchema,
    postFactoCount: countSchema,
    /** `postFactoCount` of `tripCount`, one decimal place; null when there are no trips. */
    sharePct: signedPercentSchema.nullable(),
  }),
});
export type TravelPostFactoReport = z.infer<typeof travelPostFactoReportSchema>;

// ---------------------------------------------------------------------------
// DTOs — the desk
// ---------------------------------------------------------------------------

/**
 * Turnaround and breaches of the desk's own work. A booking's turnaround is the elapsed time from the
 * moment it reached the desk to its confirmation or decline, in minutes (not working minutes — the SLA
 * itself is stored on the booking; this is what the traveller experienced). A booking that is still
 * open is counted in `bookingCount` and `breachedCount` but not in the turnaround figures. Post-facto
 * trips skip the desk and are not here.
 */
export const travelDeskPerformanceRowSchema = z.object({
  /** `YYYY-MM`, or the team id / agent's person id; null = unassigned (no team, or no agent yet). */
  key: z.string().nullable(),
  label: z.string(),
  bookingCount: countSchema,
  confirmedCount: countSchema,
  declinedCount: countSchema,
  /** Breach is marked once by the SLA sweep (EC-45); it stays marked after the booking is done. */
  breachedCount: countSchema,
  /** `breachedCount` of `bookingCount`, one decimal place; null when there are none. */
  breachRatePct: signedPercentSchema.nullable(),
  /** Bookings the desk sent back to the manager as an over-tolerance amendment (D11). */
  overspendAmendmentCount: countSchema,
  avgTurnaroundMinutes: countSchema.nullable(),
  medianTurnaroundMinutes: countSchema.nullable(),
  p90TurnaroundMinutes: countSchema.nullable(),
});
export type TravelDeskPerformanceRow = z.infer<typeof travelDeskPerformanceRowSchema>;

export const travelDeskPerformanceSchema = z.object({
  groupBy: travelDeskReportDimensionSchema,
  from: dateOnlySchema,
  to: dateOnlySchema,
  scope: travelReportScopeSchema,
  /** Ordered by booking count descending, or by period ascending. */
  rows: z.array(travelDeskPerformanceRowSchema),
});
export type TravelDeskPerformance = z.infer<typeof travelDeskPerformanceSchema>;

// ---------------------------------------------------------------------------
// DTOs — money that cancellations, refunds and credits leave behind
// ---------------------------------------------------------------------------

/**
 * What cancelled bookings cost. `chargeBorneBy` is who paid the charge after D27's default (business
 * reason: the company; personal: the employee) and any desk override; null when there was no charge.
 * `cancelReason` is why the TRIP was cancelled; null when only a booking was (an amendment replaced it).
 */
export const travelCancellationLossSchema = z.object({
  from: dateOnlySchema,
  to: dateOnlySchema,
  scope: travelReportScopeSchema,
  /** One row per currency: the whole picture. */
  totals: z.array(
    z.object({
      currency: currencySchema,
      bookingCount: countSchema,
      noShowCount: countSchema,
      chargeAmount: decimalStringSchema,
      /** The company's loss. */
      borneByCompanyAmount: decimalStringSchema,
      /** Recovered from the traveller through the recovery ladder (D4). */
      borneByEmployeeAmount: decimalStringSchema,
      refundAmount: decimalStringSchema,
    }),
  ),
  rows: z.array(
    z.object({
      cancelReason: travelCancelReasonSchema.nullable(),
      chargeBorneBy: travelChargeBorneBySchema.nullable(),
      currency: currencySchema,
      bookingCount: countSchema,
      noShowCount: countSchema,
      /** Charges whose bearer the desk changed from the default (audited, note on the trip). */
      overriddenCount: countSchema,
      chargeAmount: decimalStringSchema,
      refundAmount: decimalStringSchema,
    }),
  ),
});
export type TravelCancellationLoss = z.infer<typeof travelCancellationLossSchema>;

/** Days a refund has been outstanding since the booking was cancelled. */
export const TRAVEL_REFUND_AGEING_BUCKETS = ['0_30', '31_60', '61_90', 'over_90'] as const;
export const travelRefundAgeingBucketSchema = z.enum(TRAVEL_REFUND_AGEING_BUCKETS);
export type TravelRefundAgeingBucket = z.infer<typeof travelRefundAgeingBucketSchema>;

export const travelRefundPendingRowSchema = z.object({
  bookingId: uuidSchema,
  tripId: uuidSchema,
  tripReference: z.string(),
  personName: z.string(),
  vendorName: z.string().nullable(),
  bookingRef: z.string().nullable(),
  cancelledAt: instantSchema,
  /** What the vendor is expected to return. */
  refundAmount: decimalStringSchema,
  currency: currencySchema,
  daysPending: countSchema,
  ageing: travelRefundAgeingBucketSchema,
});
export type TravelRefundPendingRow = z.infer<typeof travelRefundPendingRowSchema>;

/**
 * Cancelled bookings whose refund has not arrived, AS OF NOW (the range does not apply), oldest first.
 * The weekly chase job works from the same set (EC-18). `totals` cover the whole set, not the page.
 */
export const travelRefundsPendingSchema = paginatedSchema(travelRefundPendingRowSchema).extend({
  asOf: dateOnlySchema,
  totals: z.array(
    z.object({
      currency: currencySchema,
      bookingCount: countSchema,
      pendingAmount: decimalStringSchema,
      /** Pending for more than 30 days — what the chase escalates. */
      overdueBookingCount: countSchema,
      overdueAmount: decimalStringSchema,
    }),
  ),
});
export type TravelRefundsPending = z.infer<typeof travelRefundsPendingSchema>;

/** `usable` has time on it, `expiring_soon` ends within 30 days, `expired` is lost unless used. */
export const TRAVEL_CREDIT_STATES = ['usable', 'expiring_soon', 'expired'] as const;
export const travelCreditStateSchema = z.enum(TRAVEL_CREDIT_STATES);
export type TravelCreditState = z.infer<typeof travelCreditStateSchema>;

export const travelUnusedCreditRowSchema = z.object({
  creditId: uuidSchema,
  personId: uuidSchema,
  personName: z.string(),
  vendorName: z.string().nullable(),
  currency: currencySchema,
  /** The credit note's face value. */
  issuedAmount: decimalStringSchema,
  /** What is still to be used. */
  remainingAmount: decimalStringSchema,
  /** The last day it can be used; null: it does not expire. */
  expiresOn: dateOnlySchema.nullable(),
  /** Negative once expired; null when it does not expire. */
  daysToExpiry: z.number().int().nullable(),
  state: travelCreditStateSchema,
});
export type TravelUnusedCreditRow = z.infer<typeof travelUnusedCreditRowSchema>;

/** Airline credits with something left, AS OF NOW (the range does not apply), soonest expiry first (EC-17). */
export const travelUnusedCreditsSchema = paginatedSchema(travelUnusedCreditRowSchema).extend({
  asOf: dateOnlySchema,
  totals: z.array(
    z.object({
      currency: currencySchema,
      creditCount: countSchema,
      usableAmount: decimalStringSchema,
      expiringSoonAmount: decimalStringSchema,
      expiredAmount: decimalStringSchema,
    }),
  ),
});
export type TravelUnusedCredits = z.infer<typeof travelUnusedCreditsSchema>;

// ---------------------------------------------------------------------------
// DTOs — vendors
// ---------------------------------------------------------------------------

/**
 * What the company spent with each vendor: company-paid bookings confirmed in the range. HRMS records
 * the cost and the invoice; it does not pay suppliers (D15, EC-43), so this is what accounts payable
 * reconciles against, not a payment record. Bookings with no vendor are one null row.
 */
export const travelVendorSpendSchema = z.object({
  from: dateOnlySchema,
  to: dateOnlySchema,
  scope: travelReportScopeSchema,
  /** Ordered by currency, then net amount descending. */
  rows: z.array(
    z.object({
      vendorId: uuidSchema.nullable(),
      vendorName: z.string(),
      vendorType: travelVendorTypeSchema.nullable(),
      currency: currencySchema,
      bookingCount: countSchema,
      /** Booked amount before tax. */
      grossAmount: decimalStringSchema,
      taxAmount: decimalStringSchema,
      cancellationChargeAmount: decimalStringSchema,
      refundAmount: decimalStringSchema,
      /** gross + tax + cancellation charges - refunds received. */
      netAmount: decimalStringSchema,
    }),
  ),
});
export type TravelVendorSpend = z.infer<typeof travelVendorSpendSchema>;

// ---------------------------------------------------------------------------
// DTOs — export
// ---------------------------------------------------------------------------

/** Returned inline, never stored — the same reasoning as the expense and payroll report exports. */
export const travelReportExportSchema = z.object({
  fileName: z.string(),
  contentType: z.literal('text/csv'),
  content: z.string(),
  rowCount: countSchema,
  generatedAt: instantSchema,
});
export type TravelReportExport = z.infer<typeof travelReportExportSchema>;

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

const errors = {
  401: errorBodySchema,
  /** No `travel:read`. */
  403: errorBodySchema,
  /** The module is off for this organisation (`module_disabled`). */
  404: errorBodySchema,
  /** `from` after `to`, or a span over 366 days. */
  422: errorBodySchema,
};

const readMeta = routeMeta('travel:read');

export const travelReportContract = c.router(
  {
    getSpendSummary: {
      method: 'GET',
      path: '/summary',
      query: travelReportQuerySchema,
      responses: { 200: travelSpendSummarySchema, ...errors },
      summary: 'Travel spend per currency for the travellers the caller may see',
      metadata: readMeta,
    },

    getSpendBreakdown: {
      method: 'GET',
      path: '/spend',
      query: travelSpendBreakdownQuerySchema,
      responses: { 200: travelSpendBreakdownSchema, ...errors },
      summary:
        'Travel spend by month, department, business unit, project, cost centre or legal entity, with cost splits honoured',
      metadata: readMeta,
    },

    getEstimateVsActual: {
      method: 'GET',
      path: '/estimate-vs-actual',
      query: travelEstimateVsActualQuerySchema,
      responses: { 200: travelEstimateVsActualSchema, ...errors },
      summary: 'Each trip’s approved estimate against what it actually cost, with the variance',
      metadata: readMeta,
    },

    getViolations: {
      method: 'GET',
      path: '/violations',
      query: travelReportQuerySchema,
      responses: { 200: travelViolationsReportSchema, ...errors },
      summary: 'How often each travel policy rule was broken, and how those trips ended',
      metadata: readMeta,
    },

    getPostFactoTrips: {
      method: 'GET',
      path: '/post-facto',
      query: travelReportPageQuerySchema,
      responses: { 200: travelPostFactoReportSchema, ...errors },
      summary: 'Trips filed after they had begun, and their share of all trips',
      metadata: readMeta,
    },

    getDeskPerformance: {
      method: 'GET',
      path: '/desk',
      query: travelDeskPerformanceQuerySchema,
      responses: { 200: travelDeskPerformanceSchema, ...errors },
      summary: 'Travel desk turnaround and SLA breaches by team, agent or month',
      metadata: readMeta,
    },

    getCancellationLoss: {
      method: 'GET',
      path: '/cancellations',
      query: travelReportQuerySchema,
      responses: { 200: travelCancellationLossSchema, ...errors },
      summary: 'Cancellation charges, who bore them, and refunds, per currency',
      metadata: readMeta,
    },

    getRefundsPending: {
      method: 'GET',
      path: '/refunds-pending',
      query: travelReportPageQuerySchema,
      responses: { 200: travelRefundsPendingSchema, ...errors },
      summary: 'Cancelled bookings whose refund has not arrived, oldest first',
      metadata: readMeta,
    },

    getUnusedCredits: {
      method: 'GET',
      path: '/unused-credits',
      query: travelReportPageQuerySchema,
      responses: { 200: travelUnusedCreditsSchema, ...errors },
      summary: 'Airline credits with an unused balance, soonest expiry first',
      metadata: readMeta,
    },

    getVendorSpend: {
      method: 'GET',
      path: '/vendors',
      query: travelVendorSpendQuerySchema,
      responses: { 200: travelVendorSpendSchema, ...errors },
      summary: 'Company-paid booking spend by vendor, per currency',
      metadata: readMeta,
    },

    /**
     * WHO IS TRAVELLING. Approved-and-live trips overlapping `from`..`to` (at most 92 days; 422
     * otherwise) for the travellers in the caller's `travel:read` population, with the traveller's
     * placement and destinations and no money. Answers "who is out of the office, and where" for HR,
     * the desk manager and managers with a grant.
     */
    getWhoIsTravelling: {
      method: 'GET',
      path: '/travelling',
      query: travellingQuerySchema,
      responses: { 200: paginatedSchema(travellingRowSchema), ...errors },
      summary: 'Who is travelling in a date range, and where',
      metadata: readMeta,
    },

    /**
     * A traveller's emergency contact, for the people who look after travellers away from base.
     * `travel:read` only, and only for someone in the caller's population (a 404 otherwise). Every
     * call is audited. `ai: 'hidden'`: it is a phone number and a relationship, never model input.
     */
    getTravellerEmergencyContact: {
      method: 'GET',
      path: '/travelling/:personId/emergency-contact',
      pathParams: z.object({ personId: uuidSchema }),
      responses: { 200: travelEmergencyContactSchema, ...errors },
      summary: "A traveller's emergency contact",
      metadata: routeMeta('travel:read', { ai: 'hidden' }),
    },

    /**
     * `ai: 'hidden'`: the response is a whole CSV file. The copilot answers from the aggregate reads
     * above and can navigate the person to the reports screen to download it. 422 also covers a
     * dataset over `TRAVEL_REPORT_EXPORT_ROW_CAP` rows. The `gst` dataset is the input-tax-credit register (D31).
     */
    exportReport: {
      method: 'GET',
      path: '/export',
      query: travelReportExportQuerySchema,
      responses: { 200: travelReportExportSchema, ...errors },
      summary: 'Download a travel report, or the GST register, as CSV',
      metadata: routeMeta('travel:read', { ai: 'hidden' }),
    },
  },
  { pathPrefix: '/travel/reports' },
);
